import {MouseEvent, useState} from "react";
import {IconButton, Popover, Tooltip, useTheme} from "@mui/material";
import {CheckCircleOutline, Delete, Description, DownloadRounded, EventBusy, MoreVert} from "@mui/icons-material";
import Stack from "@mui/material/Stack";
import {ICellRendererParams} from "ag-grid-community";
import UpdateActionIcon from "./UpdateActionIcon";
import {useTranslation} from "react-i18next";
import {Link} from "react-router-dom";

type SingleAction = {
    action: Function;
    userHasAuth?: boolean;
}

type ActionsPopoverProps = {
    view?: SingleAction;
    update?: SingleAction,
    terminate?: SingleAction,
    del?: SingleAction,
    params: ICellRendererParams,
    complete?: SingleAction
    url?: string
    downloadJimpy?: SingleAction
    download?: SingleAction
}

const ActionsPopover = ({
                            view,
                            update,
                            terminate,
                            del,
                            complete,
                            params,
                            url,
                            downloadJimpy,
                            download
                        }: ActionsPopoverProps) => {

    // Use Theme
    const theme = useTheme();

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    const styles = {
        button: {
            margin: "auto",
            color: theme.palette.accent.main
        }
    };

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;

    const Actions = () => (
        <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
                vertical: "top",
                horizontal: "left"
            }}
            sx={{marginLeft: -1}}
        >
            <Stack spacing={0.5} sx={{padding: "0.3em"}}>
                {view?.userHasAuth && view?.action &&
                    <Tooltip arrow title={t("pricing:tooltips.view")} placement={"right-start"}>
                        <IconButton
                            aria-label="view"
                            onClick={() => view.action(params.data, params.rowIndex)}
                            sx={styles.button}
                        >
                            <Description/>
                        </IconButton>
                    </Tooltip>}
                {update?.userHasAuth && update?.action && url &&
                    <Link to={url as string} target="_blank">
                        <UpdateActionIcon styles={styles.button} params={params} action={update.action}/>
                    </Link>
                }
                {update?.userHasAuth && update?.action && !url &&
                    <UpdateActionIcon styles={styles.button} params={params} action={update.action}/>
                }
                {downloadJimpy?.action && params.data.tpUnit === "DAY" &&
                    <Tooltip arrow title="downloadJimpy" placement={"right-start"}>
                        <IconButton
                            aria-label="downloadJimpy"
                            onClick={() => downloadJimpy.action(params.data)}
                            color="accent"
                            sx={styles.button}
                        >
                            <DownloadRounded/>J
                        </IconButton>
                    </Tooltip>}
                {download?.action &&
                    <Tooltip arrow title="download" placement={"right-start"}>
                        <IconButton
                            aria-label="download"
                            onClick={() => download.action(params.data)}
                            color="accent"
                            sx={styles.button}
                        >
                            <DownloadRounded/>
                        </IconButton>
                    </Tooltip>}
                {terminate?.userHasAuth && terminate?.action &&
                    <Tooltip arrow title={t("pricing:tooltips.terminate")} placement={"right-start"}>
                        <IconButton
                            aria-label="terminate"
                            onClick={() => terminate.action(params.data)}
                            sx={styles.button}
                        >
                            <EventBusy/>
                        </IconButton>
                    </Tooltip>}
                {complete?.userHasAuth && complete?.action &&
                    <Tooltip arrow title={t("pricing:tooltips.complete")} placement={"right-start"}>
                        <IconButton
                            aria-label="complete"
                            onClick={() => complete.action(params.data)}
                            sx={styles.button}
                        >
                            <CheckCircleOutline/>
                        </IconButton>
                    </Tooltip>

                }
                {del?.userHasAuth && del?.action &&
                    <Tooltip arrow title={t("pricing:tooltips.delete")} placement={"right-start"}>
                        <IconButton
                            aria-label="close"
                            onClick={() => del.action(params.data)}
                            sx={styles.button}
                        >
                            <Delete/>
                        </IconButton>
                    </Tooltip>}
            </Stack>
        </Popover>
    );

    return (
        <>
            <IconButton
                aria-label="view"
                onClick={handleClick}
                sx={{...styles.button}}
            >
                <MoreVert />
            </IconButton>
            <Actions/>
        </>
    );
};

export default ActionsPopover;